// Map Inspect Module - provides functionality to inspect layer properties on click
import { reverseGeocode } from './map.core.geocode.js';

let map = null;
let vueApp = null;
let polygonOutlineDataSource = null;
let polygonOutlineLayer = null;
let clickHandlerEnabled = true;

// Extract properties from a shape object
function extractProperties(shape) {
    if (!shape) return {};
    
    // Try multiple ways to get properties
    if (shape.getProperties) {
        return shape.getProperties();
    } else if (shape.properties) {
        return shape.properties;
    } else if (shape.data && shape.data.properties) {
        return shape.data.properties;
    } else if (shape.toJson) {
        const json = shape.toJson();
        if (json && json.properties) {
            return json.properties;
        }
    }
    
    return {};
}

// Process rendered shapes and extract features
function processRenderedShapes(renderedShapes) {
    const features = [];
    
    if (!renderedShapes || renderedShapes.length === 0) {
        return features;
    }
    
    renderedShapes.forEach(shape => {
        const properties = extractProperties(shape);
        const layer = (shape && shape.layer) ? shape.layer : null;
        
        // Only add if we have properties
        if (Object.keys(properties).length > 0) {
            features.push({
                properties: properties,
                layer: layer,
                shape: shape
            });
        }
    });
    
    return features;
}

// Parse WKT POLYGON or MULTIPOLYGON string to extract coordinates
function parseWktPolygon(wkt) {
    if (!wkt || typeof wkt !== 'string') {
        return null;
    }
    
    try {
        // Handle MULTIPOLYGON format: MULTIPOLYGON (((lng lat, lng lat, ...)))
        // or POLYGON format: POLYGON((lng lat, lng lat, ...))
        let coordString = null;
        
        if (wkt.toUpperCase().startsWith('MULTIPOLYGON')) {
            // MULTIPOLYGON format: MULTIPOLYGON (((lng lat, ...)))
            // Extract the innermost ring (first polygon's outer ring)
            const match = wkt.match(/MULTIPOLYGON\s*\(\s*\(\s*\((.*?)\)\s*\)\s*\)/i);
            if (match && match[1]) {
                coordString = match[1].trim();
            }
        } else {
            // POLYGON format: POLYGON((lng lat, ...))
            const match = wkt.match(/POLYGON\s*\(\s*\((.*?)\)\s*\)/i);
            if (match && match[1]) {
                coordString = match[1].trim();
            }
        }
        
        if (!coordString) {
            return null;
        }
        
        // Split by comma and parse coordinates
        const coordStrings = coordString.split(',');
        
        const coordinates = coordStrings.map(coordStr => {
            // Remove any leading/trailing parentheses and whitespace
            const trimmed = coordStr.trim().replace(/^[\(\s]+|[\)\s]+$/g, '');
            const parts = trimmed.split(/\s+/);
            
            if (parts.length >= 2) {
                const lng = parseFloat(parts[0]);
                const lat = parseFloat(parts[1]);
                
                if (isNaN(lng) || isNaN(lat)) {
                    return null;
                }
                
                return [lng, lat];
            }
            return null;
        }).filter(coord => coord !== null);
        
        return coordinates.length > 0 ? coordinates : null;
    } catch (err) {
        console.error('Error parsing WKT:', err);
        return null;
    }
}

// Draw bright green outline around polygon using coordinates
function drawPolygonOutline(coordinates) {
    if (!map || !coordinates || coordinates.length === 0) {
        return;
    }
    
    try {
        // Clear previous outline
        clearPolygonOutline();
        
        // Create data source if it doesn't exist
        if (!polygonOutlineDataSource) {
            polygonOutlineDataSource = new atlas.source.DataSource('polygon-outline-source');
            map.sources.add(polygonOutlineDataSource);
        }
        
        // Create line layer if it doesn't exist (for polygon outline)
        if (!polygonOutlineLayer) {
            polygonOutlineLayer = new atlas.layer.LineLayer(polygonOutlineDataSource, 'polygon-outline-layer', {
                strokeColor: '#C77BFF', // Logo purple
                strokeWidth: 3,
                strokeOpacity: 1
            });
            map.layers.add(polygonOutlineLayer);
        }
        
        // Ensure the polygon ring is closed (first point = last point)
        const closedCoordinates = [...coordinates];
        if (closedCoordinates.length > 0) {
            const firstCoord = closedCoordinates[0];
            const lastCoord = closedCoordinates[closedCoordinates.length - 1];
            
            // Check if first and last coordinates are the same (with small tolerance for floating point)
            const diffLng = Math.abs(firstCoord[0] - lastCoord[0]);
            const diffLat = Math.abs(firstCoord[1] - lastCoord[1]);
            const isClosed = diffLng < 0.000001 && diffLat < 0.000001;
            
            // If not closed, add the first coordinate at the end
            if (!isClosed) {
                closedCoordinates.push([firstCoord[0], firstCoord[1]]);
            }
        }
        
        // Create a Polygon feature from the closed coordinates
        const polygon = new atlas.data.Polygon([closedCoordinates]);
        const feature = new atlas.data.Feature(polygon);
        
        // Clear and add the new feature
        polygonOutlineDataSource.clear();
        polygonOutlineDataSource.add(feature);
        
    } catch (err) {
        console.error('Error drawing polygon outline:', err);
    }
}

// Clear polygon outline
function clearPolygonOutline() {
    if (polygonOutlineDataSource) {
        polygonOutlineDataSource.clear();
    }
}

// Handle map click
async function handleMapClick(e) {
    // Use window references for optional dependencies
    const MapDrawing = window.MapDrawing;
    if (MapDrawing?.shouldSuppressInspect?.()) return;
    if (MapDrawing?.isDrawingActive?.()) return;  
    if (!map || !clickHandlerEnabled) return;

    // Close banner when map is clicked
    if (window.vueAppInstance && typeof window.vueAppInstance.closeBanner === 'function') {
        window.vueAppInstance.closeBanner();
    }

    const position = e.position; // [lng, lat]
    const [lng, lat] = position;
    
    // Get all features at the clicked position using Azure Maps API
    let allFeatures = [];
    const MapLayers = window.MapLayers;

    let renderedShapes = [];
    try {
        renderedShapes = map.layers.getRenderedShapes(position);
        allFeatures = processRenderedShapes(renderedShapes);
    } catch (err) {
        console.debug('Error getting rendered shapes:', err);
    }

    // Detect overlay membership from visible overlay layers (always in pipeline at 0.01 when off)
    const isHistoricDistrict = (renderedShapes && renderedShapes.length > 0) && renderedShapes.some(s => s.layer && s.layer.id && s.layer.id === 'wilmington-historic-zoning-fill');
    const isHistoric1945 = (renderedShapes && renderedShapes.length > 0) && renderedShapes.some(s => s.layer && s.layer.id && s.layer.id === 'wilmington-1945-zoning-fill');
    const isWithinHeightOverlay = (renderedShapes && renderedShapes.length > 0) && renderedShapes.some(s => s.layer && s.layer.id && s.layer.id === 'tocb-heightoverlay-zoning-fill');
    
    // Extract parcel details using MapLayers business logic
    const parcelDetails = MapLayers.extractParcelDetails(allFeatures);
    parcelDetails.isWithinHistoricDistrict = isHistoricDistrict;
    parcelDetails.isWithinHistoric1945 = isHistoric1945;
    parcelDetails.isWithinHeightOverlay = isWithinHeightOverlay;
    let parcelsFromBoundary = [];
    
    // If we have a PID and clicked on nhc_parcels layer, fetch boundary and draw outline
    if (parcelDetails && parcelDetails.pid) {
        // Check if we clicked on nhc_parcels layer
        let clickedOnNhcParcels = false;
        try {
            const renderedShapes = map.layers.getRenderedShapes(position);
            for (let i = 0; i < renderedShapes.length; i++) {
                const shape = renderedShapes[i];
                const layerId = shape && shape.layer && shape.layer.id ? shape.layer.id : '';
                if (layerId && layerId.includes('nhc_parcels')) {
                    clickedOnNhcParcels = true;
                    break;
                }
            }
        } catch (err) {
            console.debug('Error checking layer:', err);
        }
        
        if (clickedOnNhcParcels) {
            // Fetch boundary from API
            try {
                const response = await ApiClient.get(`/api/Parcel/boundary?pid=${encodeURIComponent(parcelDetails.pid)}`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.wkt) {
                        // Parse WKT and draw outline
                        const coordinates = parseWktPolygon(data.wkt);
                        if (coordinates) {
                            drawPolygonOutline(coordinates);
                        }
                    }
                    if (data.parcels) {
                        parcelsFromBoundary = data.parcels;
                    }
                    // Store total frontage in parcelDetails if available
                    if (data.totalFrontageFeet !== null && data.totalFrontageFeet !== undefined) {
                        parcelDetails.totalFrontageFeet = data.totalFrontageFeet;
                    }
                    // Store area in square feet if available
                    if (data.areaSquareFeet !== null && data.areaSquareFeet !== undefined) {
                        parcelDetails.areaSquareFeet = data.areaSquareFeet;
                    }
                    // Display side length labels if available
                    const MapParcelGeometry = window.MapParcelGeometry;
                    if (data.sideInfo && MapParcelGeometry && MapParcelGeometry.displaySideLabels) {
                        MapParcelGeometry.displaySideLabels(data.sideInfo);
                        // Store sideInfo in Vue for 3D terrain view
                        if (vueApp) {
                            vueApp.parcelSideInfo = data.sideInfo;
                        }
                    }
                    // Store boundary coordinates for 3D terrain view
                    if (vueApp && data.wkt) {
                        const coordinates = parseWktPolygon(data.wkt);
                        if (coordinates) {
                            vueApp.parcelBoundaryCoordinates = coordinates;
                        }
                    }
                }
            } catch (err) {
                console.error('Error fetching parcel boundary:', err);
            }
        }
    }

    // Reverse geocode to get address
    let address = null;
    try {
        address = await reverseGeocode(lng, lat);
    } catch (err) {
        console.debug('Error reverse geocoding:', err);
    }

    // Send data to Vue
    if (vueApp && vueApp.showInspectionData) {
        vueApp.showInspectionData(parcelDetails, address, parcelsFromBoundary);
    }
}

// Load setbacks for a given zoning district
async function loadSetbacks(zone) {
    if (!vueApp) {
        console.error('Vue app not initialized');
        return;
    }

    if (!zone) {
        vueApp.setbacks = {
            front: [],
            side: [],
            rear: [],
            corner: []
        };
        return;
    }

    vueApp.setbacksLoading = true;
    vueApp.showMoreSetbacks = false; // Reset the collapsible state
    try {
        // Get municipality ID from the current parcel
        const municipalityId = vueApp.currentMunicipalityId || 1;
        const response = await ApiClient.get(`/api/setbacks/by-zone?zone=${encodeURIComponent(zone)}&municipalityId=${municipalityId}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        vueApp.setbacks = {
            front: data.front || [],
            side: data.side || [],
            rear: data.rear || [],
            corner: data.corner || []
        };
    } catch (err) {
        console.error('Error loading setbacks:', err);
        vueApp.setbacks = {
            front: [],
            side: [],
            rear: [],
            corner: []
        };
    } finally {
        vueApp.setbacksLoading = false;
    }
}

// Get base setback for a given type
function getBaseSetback(type) {
    if (!vueApp || !vueApp.setbacks) {
        return null;
    }
    const setbacks = vueApp.setbacks[type] || [];
    return setbacks.find(s => s.is_base === true) || null;
}

// Get common setbacks (is_common but not is_base) for a given type
function getCommonSetbacks(type) {
    if (!vueApp || !vueApp.setbacks) {
        return [];
    }
    const setbacks = vueApp.setbacks[type] || [];
    return setbacks.filter(s => s.is_common === true && s.is_base !== true);
}

// Get non-common setbacks for a given type
function getNonCommonSetbacks(type) {
    if (!vueApp || !vueApp.setbacks) {
        return [];
    }
    const setbacks = vueApp.setbacks[type] || [];
    return setbacks.filter(s => s.is_common !== true || s.is_base === true);
}

// Check if there are any base setbacks
function hasBaseSetbacks() {
    return getBaseSetback('front') || 
           getBaseSetback('side') || 
           getBaseSetback('rear') || 
           getBaseSetback('corner');
}

// Check if there are any common setbacks
function hasCommonSetbacks() {
    return getCommonSetbacks('front').length > 0 ||
           getCommonSetbacks('side').length > 0 ||
           getCommonSetbacks('rear').length > 0 ||
           getCommonSetbacks('corner').length > 0;
}

// Toggle show more setbacks
function toggleShowMoreSetbacks() {
    if (!vueApp) {
        return;
    }
    vueApp.showMoreSetbacks = !vueApp.showMoreSetbacks;
}

// Load dev standards for a given zoning district
async function loadDevStandards(zone, municipalityId) {
    if (!vueApp) {
        console.error('Vue app not initialized');
        return;
    }

    if (!zone) {
        vueApp.devStandards = {};
        return;
    }

    vueApp.devStandardsLoading = true;
    try {
        const muniId = municipalityId || vueApp.currentMunicipalityId || 1;
        const response = await ApiClient.get(`/api/devstandards/by-zone?zone=${encodeURIComponent(zone)}&municipalityId=${muniId}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        vueApp.devStandards = data || {};
    } catch (err) {
        console.error('Error loading dev standards:', err);
        vueApp.devStandards = {};
    } finally {
        vueApp.devStandardsLoading = false;
    }
}

// Get base standard for a given category
function getBaseStandard(category) {
    if (!vueApp || !vueApp.devStandards) {
        return null;
    }
    const standards = vueApp.devStandards[category] || [];
    if (!Array.isArray(standards)) {
        return null;
    }
    return standards.find(s => s.is_base === true) || null;
}

// Get non-base standards for a given category (similar to getNonCommonSetbacks logic)
// This includes base items so they appear in both table and detail section
function getNonBaseStandards(category) {
    if (!vueApp || !vueApp.devStandards) {
        return [];
    }
    const standards = vueApp.devStandards[category] || [];
    if (!Array.isArray(standards)) {
        return [];
    }
    // Return all standards (including base) - matching setbacks behavior
    return standards;
}

// Get all standard categories from devStandards
function getStandardCategories() {
    if (!vueApp || !vueApp.devStandards) {
        return [];
    }
    return Object.keys(vueApp.devStandards).filter(key => {
        const standards = vueApp.devStandards[key];
        return Array.isArray(standards) && standards.length > 0;
    });
}

// Check if there are any base standards
function hasBaseStandards() {
    if (!vueApp || !vueApp.devStandards) {
        return false;
    }
    const categories = getStandardCategories();
    return categories.some(cat => getBaseStandard(cat) !== null);
}

// Public API
const MapInspect = {
    initialize: function(mapInstance, vueAppInstance) {
        map = mapInstance;
        vueApp = vueAppInstance;
        
        // Add click event listener
        map.events.add('click', handleMapClick);
        clickHandlerEnabled = true;
    },

    enableClickHandler: function() {
        clickHandlerEnabled = true;
    },

    disableClickHandler: function() {
        clickHandlerEnabled = false;
    },

    loadSetbacks: function(zone) {
        return loadSetbacks(zone);
    },

    loadDevStandards: function(zone, municipalityId) {
        return loadDevStandards(zone, municipalityId);
    },

    getBaseSetback: function(type) {
        return getBaseSetback(type);
    },

    getCommonSetbacks: function(type) {
        return getCommonSetbacks(type);
    },

    getNonCommonSetbacks: function(type) {
        return getNonCommonSetbacks(type);
    },

    hasBaseSetbacks: function() {
        return hasBaseSetbacks();
    },

    hasCommonSetbacks: function() {
        return hasCommonSetbacks();
    },

    toggleShowMoreSetbacks: function() {
        toggleShowMoreSetbacks();
    },

    getBaseStandard: function(category) {
        return getBaseStandard(category);
    },

    getNonBaseStandards: function(category) {
        return getNonBaseStandards(category);
    },

    getStandardCategories: function() {
        return getStandardCategories();
    },

    hasBaseStandards: function() {
        return hasBaseStandards();
    },

    cleanup: function() {
        if (map) {
            map.events.remove('click', handleMapClick);
        }
        clearPolygonOutline();
        if (polygonOutlineLayer && map) {
            map.layers.remove(polygonOutlineLayer);
            polygonOutlineLayer = null;
        }
        if (polygonOutlineDataSource && map) {
            map.sources.remove(polygonOutlineDataSource);
            polygonOutlineDataSource = null;
        }
        // Clear side labels when cleaning up
        const MapParcelGeometry = window.MapParcelGeometry;
        if (MapParcelGeometry && MapParcelGeometry.clearSideLabels) {
            MapParcelGeometry.clearSideLabels();
        }
    }
};

// Export for ES6 modules
export default MapInspect;

// Make it globally accessible for backward compatibility
window.MapInspect = MapInspect;
